<script setup lang="ts">
import { useAppStore } from '@/stores/app';
import { motion, useReducedMotion } from 'motion-v';
import { computed, onBeforeUnmount, onDeactivated, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import { useTypewriter } from '@/use/typewriter';

const appStore = useAppStore();
const { t } = useI18n();
const reducedMotion = useReducedMotion();

const props = defineProps<{
  modelValue: string;
  placeholder?: string;
}>();

const emit = defineEmits(['update:modelValue', 'focus', 'blur']);

const inputEl = ref<HTMLInputElement>();
const searchText = ref(props.modelValue);
const isFocused = ref(false);

// A native placeholder is a plain string: the default one is drawn by an
// overlay instead, so the searched thing can be typed out word after word.
const typesPlaceholder = computed(() => !props.placeholder && !reducedMotion.value);
const nativePlaceholder = computed(() =>
  typesPlaceholder.value ? '' : props.placeholder || t('search.placeholder'),
);
// Once focused, the field is left empty for the user's own caret.
const showsTypedPlaceholder = computed(() => typesPlaceholder.value && !searchText.value && !isFocused.value);
const { text: typedPlaceholder } = useTypewriter(
  () => [t('search.placeholderRoute'), t('search.placeholderStop')],
  () => showsTypedPlaceholder.value,
);

// A page leaving while its field is focused never gets the blur, whether it is
// unmounted or only deactivated by keep-alive: without this the tab bar would
// stay hidden on the next page.
function releaseFocusFlag() {
  if (appStore.isSearchBarFocused) appStore.isSearchBarFocused = false;
}
onBeforeUnmount(releaseFocusFlag);
onDeactivated(() => {
  inputEl.value?.blur();
  releaseFocusFlag();
});

watch(searchText, newValue => {
  emit('update:modelValue', newValue.trim());
});

// The parent can reset or replace the text (e.g. when the search closes).
watch(
  () => props.modelValue,
  value => {
    if (value !== searchText.value.trim()) searchText.value = value;
  },
);

defineExpose({
  clear,
  focus: () => inputEl.value?.focus(),
  blur: () => inputEl.value?.blur(),
});

function onFocus() {
  isFocused.value = true;
  appStore.isSearchBarFocused = true;
  emit('focus');
}

function onBlur() {
  isFocused.value = false;
  appStore.isSearchBarFocused = false;
  emit('blur');
}

function clear() {
  searchText.value = '';
}
</script>

<template>
  <div class="searchbar" :class="{ 'searchbar--active': searchText }">
    <BaseIcon name="search" class="searchbar__icon" />
    <div class="searchbar__field">
      <input
        ref="inputEl"
        v-model="searchText"
        type="text"
        class="searchbar__input"
        :aria-label="props.placeholder || t('search.placeholder')"
        :placeholder="nativePlaceholder"
        @focus="onFocus()"
        @blur="onBlur()"
      />
      <span v-if="showsTypedPlaceholder" class="searchbar__placeholder" aria-hidden="true">
        <span class="searchbar__placeholder-text">
          {{ t('search.placeholderPrefix') }}&nbsp;{{ typedPlaceholder }}
        </span>
        <motion.span
          class="searchbar__caret"
          :animate="{ opacity: [1, 1, 0, 0] }"
          :transition="{ duration: 5, ease: 'linear', repeat: Infinity, times: [0, 2.5, 2.5, 5] }"
        />
      </span>
    </div>
    <button
      v-if="searchText"
      :aria-label="$t('common.clear')"
      class="searchbar__clear"
      type="button"
      @mousedown.prevent
      @click="clear()"
    >
      <BaseIcon name="eraser" />
    </button>
  </div>
</template>

<style lang="scss">
.searchbar {
  align-items: center;
  background-color: $color-white;
  border: 1px solid $color-border-medium;
  border-radius: $radius-pill;
  box-shadow: $shadow-soft;
  display: flex;
  gap: 6px;
  margin-bottom: 1.5rem;
  padding: 13px $spacing-3 13px $spacing-5;

  &:focus-within,
  &--active {
    border-color: $color-neutral-highest;
    box-shadow: inset 0 0 0 1px $color-neutral-highest;
  }

  &__icon {
    color: $color-neutral-highest;
    flex-shrink: 0;
    font-size: 24px;
  }

  &__field {
    display: flex;
    flex: 1;
    min-width: 0;
    position: relative;
  }

  &__input {
    background: none;
    border: none;
    color: $color-neutral-highest;
    flex: 1;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    min-width: 0;
    outline: none;
    padding: 0;

    &::placeholder {
      color: $color-neutral-higher;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__placeholder {
    align-items: center;
    color: $color-neutral-higher;
    display: flex;
    font: $label-medium;
    inset: 0;
    letter-spacing: $label-medium-spacing;
    overflow: hidden;
    pointer-events: none;
    position: absolute;
    white-space: nowrap;
  }

  &__placeholder-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__caret {
    background-color: currentcolor;
    flex-shrink: 0;
    height: 1em;
    margin-left: 1px;
    width: 1.5px;
  }

  &__clear {
    align-items: center;
    background: none;
    border: none;
    color: $color-neutral-highest;
    cursor: pointer;
    display: flex;
    flex-shrink: 0;
    font-size: 20px;
    justify-content: center;
    padding: 0;
  }
}
</style>
